import { ScheduleOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import TOUAvatar from 'switch-fe-shared/components/Avatars/TOUAvatar';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import type {
  PpaSiteTouLine,
  PpaSiteTouSavings,
} from 'switch-shared/modules/dashboard/ppa-site.dto';
import { TimeOfUsePeriods } from 'switch-shared/types/eskom';
import {
  formatCurrencyNew,
  formatNumber,
} from 'switch-shared/utils/formatters';

import { monthKeyLabel } from '../../utils/solarPresentation';
import SolarCardState, { type SolarCardStatus } from './SolarCardState';
import SolarSummaryTable from './SolarSummaryTable';

const WINDOW_LABELS: Record<string, string> = {
  [TimeOfUsePeriods.Peak]: 'Peak',
  [TimeOfUsePeriods.Standard]: 'Standard',
  [TimeOfUsePeriods.OffPeak]: 'Off-peak',
  all: 'All hours',
};

const isPeriod = (window: string): window is TimeOfUsePeriods =>
  Object.values<string>(TimeOfUsePeriods).includes(window);

interface Props {
  status: SolarCardStatus;

  /** Absent when the site lacks a grid or PPA tariff. */
  touSavings?: PpaSiteTouSavings;
}

/** The last closed month's solar used on site per time-of-use period, priced on the grid and PPA tariffs. */
const SolarTouSavingsCard = ({ status, touSavings }: Props) => {
  const money = (value: number, decimals: number) =>
    touSavings ? formatCurrencyNew(value, touSavings.currency, decimals) : '';
  const total = (pick: (line: PpaSiteTouLine) => number) =>
    touSavings?.lines.reduce((sum, line) => sum + pick(line), 0) ?? 0;

  return (
    <WidgetCard
      title='Solar PV Saving Summary'
      subheader={
        touSavings
          ? `${monthKeyLabel(touSavings.key)} | Solar used on site, energy charges ex VAT`
          : undefined
      }
      avatar={<ScheduleOutlined />}
      cardContentProps={{
        sx: { display: 'flex', flexDirection: 'column', pb: '16px !important' },
      }}
    >
      <SolarCardState
        status={status === 'ready' && !touSavings ? 'no-tariffs' : status}
      >
        {touSavings && (
          <SolarSummaryTable
            columns={[
              {
                key: 'window',
                header: 'TOU',
                render: (line: PpaSiteTouLine) => (
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1}
                    sx={{ '& .MuiAvatar-root': { m: 0 } }}
                  >
                    {isPeriod(line.window) && <TOUAvatar type={line.window} />}
                    {WINDOW_LABELS[line.window] ?? line.window}
                  </Box>
                ),
              },
              {
                key: 'kwh',
                header: 'Used on site (kWh)',
                align: 'right',
                render: (line) => formatNumber(line.kwh, 0),
              },
              {
                key: 'gridRate',
                header: 'Grid per kWh',
                align: 'right',
                render: (line) => money(line.gridRate, 3),
              },
              {
                key: 'ppaRate',
                header: 'PPA per kWh',
                align: 'right',
                render: (line) => money(line.ppaRate, 3),
              },
              {
                key: 'saving',
                header: 'Saving',
                align: 'right',
                render: (line) => money(line.saving, 2),
              },
            ]}
            rows={touSavings.lines}
            getRowKey={(line) => line.window}
            footer={[
              'Energy saving',
              formatNumber(
                total((line) => line.kwh),
                0
              ),
              '',
              '',
              money(
                total((line) => line.saving),
                2
              ),
            ]}
            caption='Grid value less PPA cost of the same energy. Demand, fixed and reactive charges are not included.'
          />
        )}
      </SolarCardState>
    </WidgetCard>
  );
};

export default SolarTouSavingsCard;
